import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Col, Input, List, Row, Space, Tag, Typography } from 'antd';
import {
  createNativeRequestId,
  EVENT_ACTION_RESULT,
  EVENT_BLE_SCAN_RESULTS,
  EVENT_DEVICE_ERROR,
  EVENT_DEVICE_STATE_CHANGED,
  getNativeDeviceControlState,
  invokeConnectThinklet,
  invokeDisconnectThinklet,
  invokeOpenAppSettings,
  invokeRequestPermissions,
  invokeStartBleScan,
  invokeStartTracking,
  invokeStopTracking,
  isNativeDeviceControlEnabled,
  NativeActionResult,
  NativeDeviceControlState,
  NativeDeviceError,
  ThinkletScanResult,
} from '../native/deviceBridge';

const { Text, Title } = Typography;

const parseEventDetail = <T,>(event: Event): T | null => {
  const customEvent = event as CustomEvent<unknown>;
  const detail = customEvent.detail;
  if (!detail) return null;

  if (typeof detail === 'string') {
    try {
      return JSON.parse(detail) as T;
    } catch {
      return null;
    }
  }
  return detail as T;
};

const NativeDevicePage: React.FC = () => {
  const [isNativeAvailable, setIsNativeAvailable] = useState<boolean>(isNativeDeviceControlEnabled());
  const [deviceState, setDeviceState] = useState<NativeDeviceControlState>(getNativeDeviceControlState());
  const [scanResults, setScanResults] = useState<ThinkletScanResult[]>([]);
  const [targetDeviceId, setTargetDeviceId] = useState<string>('');
  const [actionResults, setActionResults] = useState<NativeActionResult[]>([]);
  const [errorResults, setErrorResults] = useState<NativeDeviceError[]>([]);

  const refreshNativeState = useCallback(() => {
    const enabled = isNativeDeviceControlEnabled();
    setIsNativeAvailable(enabled);
    setDeviceState(getNativeDeviceControlState());
  }, []);

  useEffect(() => {
    refreshNativeState();
  }, [refreshNativeState]);

  useEffect(() => {
    const onStateChanged = (event: Event) => {
      const detail = parseEventDetail<NativeDeviceControlState>(event);
      if (detail) {
        setDeviceState(detail);
      } else {
        refreshNativeState();
      }
    };

    const onScanResults = (event: Event) => {
      const detail = parseEventDetail<{ requestId?: string; results?: ThinkletScanResult[] }>(event);
      setScanResults(detail?.results || []);
    };

    const onActionResult = (event: Event) => {
      const detail = parseEventDetail<NativeActionResult>(event);
      if (!detail) return;
      setActionResults((prev) => [detail, ...prev].slice(0, 20));
      refreshNativeState();
    };

    const onDeviceError = (event: Event) => {
      const detail = parseEventDetail<NativeDeviceError>(event);
      if (!detail) return;
      setErrorResults((prev) => [detail, ...prev].slice(0, 20));
      refreshNativeState();
    };

    window.addEventListener(EVENT_DEVICE_STATE_CHANGED, onStateChanged as EventListener);
    window.addEventListener(EVENT_BLE_SCAN_RESULTS, onScanResults as EventListener);
    window.addEventListener(EVENT_ACTION_RESULT, onActionResult as EventListener);
    window.addEventListener(EVENT_DEVICE_ERROR, onDeviceError as EventListener);

    return () => {
      window.removeEventListener(EVENT_DEVICE_STATE_CHANGED, onStateChanged as EventListener);
      window.removeEventListener(EVENT_BLE_SCAN_RESULTS, onScanResults as EventListener);
      window.removeEventListener(EVENT_ACTION_RESULT, onActionResult as EventListener);
      window.removeEventListener(EVENT_DEVICE_ERROR, onDeviceError as EventListener);
    };
  }, [refreshNativeState]);

  useEffect(() => {
    if (targetDeviceId.trim().length > 0) return;
    if (deviceState.pending_connect_target_device_id) {
      setTargetDeviceId(deviceState.pending_connect_target_device_id);
      return;
    }
    if (deviceState.connected_thinklet_device_id) {
      setTargetDeviceId(deviceState.connected_thinklet_device_id);
    }
  }, [deviceState.connected_thinklet_device_id, deviceState.pending_connect_target_device_id, targetDeviceId]);

  const handleScan = () => {
    const requestId = createNativeRequestId('scan');
    invokeStartBleScan(requestId);
  };

  const handleConnect = () => {
    const normalized = targetDeviceId.trim();
    if (!normalized) return;
    const requestId = createNativeRequestId('connect');
    invokeConnectThinklet(requestId, normalized);
  };

  const handleDisconnect = () => {
    const requestId = createNativeRequestId('disconnect');
    invokeDisconnectThinklet(requestId);
  };

  const handleStartTracking = () => {
    const requestId = createNativeRequestId('startTracking');
    invokeStartTracking(requestId);
  };

  const handleStopTracking = () => {
    const requestId = createNativeRequestId('stopTracking');
    invokeStopTracking(requestId);
  };

  const handleRequestPermissions = () => {
    const requestId = createNativeRequestId('requestPermissions');
    invokeRequestPermissions(requestId);
  };

  const handleOpenSettings = () => {
    const requestId = createNativeRequestId('openAppSettings');
    invokeOpenAppSettings(requestId);
  };

  const missingPermissions = useMemo(() => deviceState.missing_permissions || [], [deviceState.missing_permissions]);
  const latestAction = actionResults.length > 0 ? actionResults[0] : null;
  const latestError = errorResults.length > 0 ? errorResults[0] : null;

  if (!isNativeAvailable) {
    return (
      <Card>
        <Alert
          type="warning"
          showIcon
          message="このページは Android アプリ（WebView）アクセス時のみ利用できます"
          description="通常のブラウザアクセスでは Android ネイティブ機能が利用できないため、表示を制限しています。"
        />
      </Card>
    );
  }

  return (
    <Space direction="vertical" style={{ width: '100%' }} size={16}>
      <Card>
        <Title level={4} style={{ marginBottom: 12 }}>
          アプリ専用デバイス操作
        </Title>
        <Text type="secondary">
          WebView 経由で Android ネイティブ機能（BLE/GPS Tracking）を操作します。
        </Text>
      </Card>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title="現在状態">
            <Space direction="vertical" style={{ width: '100%' }} size={8}>
              <div><Text strong>trusted origin:</Text> <Tag>{String(deviceState.trusted_origin === true)}</Tag></div>
              <div><Text strong>deviceId:</Text> <Text code>{deviceState.device_id || '(none)'}</Text></div>
              <div><Text strong>BLE state:</Text> <Tag color="blue">{deviceState.ble_state || 'UNKNOWN'}</Tag></div>
              <div><Text strong>connected THINKLET:</Text> <Text code>{deviceState.connected_thinklet_device_id || '(none)'}</Text></div>
              <div><Text strong>tracking:</Text> <Tag color={deviceState.tracking ? 'green' : 'default'}>{deviceState.tracking ? 'ON' : 'OFF'}</Tag></div>
            </Space>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="権限状態">
            <Space direction="vertical" style={{ width: '100%' }} size={12}>
              {missingPermissions.length === 0 ? (
                <Alert type="success" showIcon message="必要権限は許可済みです" />
              ) : (
                <Alert
                  type="warning"
                  showIcon
                  message="未許可の権限があります"
                  description={missingPermissions.join(', ')}
                />
              )}
              <Space>
                <Button onClick={handleRequestPermissions}>権限を要求</Button>
                <Button onClick={handleOpenSettings}>設定アプリを開く</Button>
              </Space>
            </Space>
          </Card>
        </Col>
      </Row>

      <Card title="THINKLET 接続操作">
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          <Space.Compact style={{ width: '100%' }}>
            <Input
              placeholder="接続先 THINKLET の deviceId"
              value={targetDeviceId}
              onChange={(event) => setTargetDeviceId(event.target.value)}
            />
            <Button type="primary" onClick={handleConnect} disabled={targetDeviceId.trim().length === 0}>
              接続
            </Button>
          </Space.Compact>
          <Space>
            <Button onClick={handleScan}>周囲をスキャン</Button>
            <Button onClick={handleDisconnect}>切断</Button>
          </Space>
          <List
            size="small"
            bordered
            header="スキャン結果"
            dataSource={scanResults}
            locale={{ emptyText: 'スキャン結果はまだありません' }}
            renderItem={(item) => (
              <List.Item>
                <Space direction="vertical" size={0}>
                  <Text>deviceId: <Text code>{item.device_id || '(unknown)'}</Text></Text>
                  <Text type="secondary">name: {item.name || '(none)'} / address: {item.address || '(none)'}</Text>
                </Space>
              </List.Item>
            )}
          />
        </Space>
      </Card>

      <Card title="GPS Tracking">
        <Space>
          <Button type="primary" onClick={handleStartTracking}>Start Tracking</Button>
          <Button danger onClick={handleStopTracking}>Stop Tracking</Button>
        </Space>
      </Card>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title="最新アクション結果">
            {latestAction ? (
              <Space direction="vertical" size={6}>
                <Text><Text strong>status:</Text> {latestAction.status || '(none)'}</Text>
                <Text><Text strong>code:</Text> {latestAction.code || '(none)'}</Text>
                <Text><Text strong>message:</Text> {latestAction.message || '(none)'}</Text>
                <Text><Text strong>requestId:</Text> <Text code>{latestAction.requestId || '(none)'}</Text></Text>
              </Space>
            ) : (
              <Text type="secondary">まだ結果はありません。</Text>
            )}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="最新エラー">
            {latestError ? (
              <Space direction="vertical" size={6}>
                <Text><Text strong>code:</Text> {latestError.code || '(none)'}</Text>
                <Text><Text strong>message:</Text> {latestError.message || '(none)'}</Text>
                <Text><Text strong>requestId:</Text> <Text code>{latestError.requestId || '(none)'}</Text></Text>
              </Space>
            ) : (
              <Text type="secondary">エラーはまだありません。</Text>
            )}
          </Card>
        </Col>
      </Row>
    </Space>
  );
};

export default NativeDevicePage;

